import type { AdminPaymentSummary, PaymentMethodCode, PaymentVerificationStatus } from '@/types/api'
import type { BadgeTone } from '@/components/ui/status-tone'

/**
 * Machine values in, operator-facing labels out.
 *
 * The API sends stable enum names and the dashboard shows friendly text; this
 * is the only place the two are connected. Anything unrecognised falls through
 * to the raw value rather than to an empty cell — a booking whose method the
 * backend has never written should read as whatever it actually says, not as a
 * blank an operator has to go and query the database over.
 */

const METHOD_LABELS: Record<PaymentMethodCode, string> = {
  CARD: 'Credit card',
  CASH: 'Cash',
  INSTAPAY: 'Instapay',
}

const VERIFICATION_LABELS: Record<PaymentVerificationStatus, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Awaiting review',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
}

const VERIFICATION_TONES: Record<PaymentVerificationStatus, BadgeTone> = {
  NOT_REQUIRED: 'neutral',
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'danger',
}

export function describePaymentMethod(payment: Pick<AdminPaymentSummary, 'paymentMethodCode' | 'paymentMethod'>): string {
  if (payment.paymentMethodCode) return METHOD_LABELS[payment.paymentMethodCode]
  return payment.paymentMethod ?? '—'
}

export function describeVerificationStatus(status: PaymentVerificationStatus | null): string {
  if (!status) return VERIFICATION_LABELS.NOT_REQUIRED
  return VERIFICATION_LABELS[status] ?? status
}

export function verificationTone(status: PaymentVerificationStatus | null): BadgeTone {
  if (!status) return 'neutral'
  return VERIFICATION_TONES[status] ?? 'neutral'
}

/**
 * How much of the booking points covered, said in words.
 *
 * Three distinct cases, because "partially paid with points" and "fully paid
 * with points" mean very different things to an operator deciding whether to
 * chase a payment.
 */
export function describePointsCoverage(payment: AdminPaymentSummary): string | null {
  if (payment.fullyCoveredByPoints) return 'Fully paid with points'
  if (payment.partiallyCoveredByPoints) return 'Partly paid with points'
  return null
}

/**
 * Whether this payment is waiting on an operator to look at it.
 *
 * Distinct from "pending": an Instapay booking is pending from the moment it is
 * created, including before the customer has uploaded anything. Only the ones
 * with proof attached are actually actionable.
 */
export function awaitsReview(payment: AdminPaymentSummary): boolean {
  return payment.canVerify && payment.verificationStatus === 'PENDING' && payment.hasProof
}

/** Pending, but there is nothing yet for an operator to look at. */
export function awaitsProof(payment: AdminPaymentSummary): boolean {
  return payment.canVerify && payment.verificationStatus === 'PENDING' && !payment.hasProof
}

const POINTS_TYPE_LABELS: Record<string, string> = {
  REFUND_CREDIT: 'Refund credited as points',
  BOOKING_REDEMPTION: 'Points applied to this booking',
  BOOKING_RESTORE: 'Points returned',
  ADMIN_ADJUSTMENT: 'Adjusted by an administrator',
}

export function describePointsTransactionType(type: string | null): string {
  if (!type) return 'Points movement'
  return POINTS_TYPE_LABELS[type] ?? type
}

/**
 * Money, formatted for display.
 *
 * Takes the major-unit decimal the server already computed. The dashboard never
 * converts from minor units itself — that is arithmetic on money in a
 * JavaScript number, which is exactly what the backend's minor-unit
 * representation exists to avoid.
 */
export function formatMoney(amount: number | null | undefined, currency = 'EGP'): string {
  if (amount === null || amount === undefined) return '—'
  return `${currency} ${amount.toLocaleString('en-EG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}
